import { createSignal } from "solid-js";

import { closestSelector } from "@/lib/gestures/guards";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { motionDurationMs } from "@/lib/gestures/reduced-motion";
import { pointerHandlers } from "@/lib/gestures/solid";
import { KDS_LINE_SWIPE } from "@/lib/gestures/presets";

interface KdsLineSwipeOptions {
  /** Whether the line can still advance (i.e. not yet `served`). */
  enabled: () => boolean;
  /** Advance the line to the next kitchen status (bump). */
  onAdvance: () => void;
  /** Width of the swiped surface, for the slide distance. */
  getSurfaceRef: () => HTMLElement | undefined;
}

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
  fling: FLING,
} = KDS_LINE_SWIPE;

/**
 * Swipe-to-advance (bump) for a KDS line. Swiping right past the commit
 * threshold (or flinging) bumps the line to the next cooking status, then snaps
 * back so the line stays in place with its new status chip. Only rightward
 * travel commits; left-drag rubber-bands and bounces back.
 */
export function useKdsLineSwipe(options: KdsLineSwipeOptions) {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let hasSwiped = false;

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_MS,
    fling: FLING,
    canStart: ({ target }) => {
      if (!options.enabled()) {
        return false;
      }
      return !closestSelector(target, 'button, a, input, select, [data-no-swipe="true"]');
    },
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      // Only allow rightward travel; rubber-band the rest.
      const dx = Math.max(sample.dx, 0);
      setTx(applyRubberBand(dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR));
    },
    onEnd: (sample) => {
      hasSwiped = true;
      setIsSwiping(false);
      setIsAnimating(true);

      const finalTx = tx();
      const commitRight =
        finalTx > COMMIT_THRESHOLD_PX || (sample.isFling && sample.direction === "right");

      if (commitRight) {
        const width = options.getSurfaceRef()?.clientWidth ?? FALLBACK_WIDTH_PX;
        setTx(width);
        setTimeout(() => {
          options.onAdvance();
          // Snap back: the line stays on the board with its new status.
          setIsAnimating(false);
          setTx(0);
        }, motionDurationMs(COMMIT_ANIMATION_MS));
      } else {
        setTx(0);
        setTimeout(() => {
          setIsAnimating(false);
        }, motionDurationMs(CANCEL_ANIMATION_MS));
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      setTx(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, motionDurationMs(CANCEL_ANIMATION_MS));
    },
  });

  const base = pointerHandlers(recognizer);

  return {
    tx,
    isSwiping,
    isAnimating,
    handlePointerDown: base.onPointerDown,
    handlePointerMove: base.onPointerMove,
    handlePointerUp: base.onPointerUp,
    handlePointerCancel: base.onPointerCancel,
    getHasSwiped: () => hasSwiped,
    resetHasSwiped: () => {
      hasSwiped = false;
    },
    COMMIT_THRESHOLD_PX,
  };
}
